/**
 * Softphone (pilote Amazon Connect, 27/10/2026) – contrat commun des moteurs : le moteur Amazon Connect
 * (moteur-connect.ts : bibliothèque amazon-connect-streams, CCP masqué dans la page, son par WebRTC) et le moteur de la
 * maquette (moteur-maquette.ts : appels simulés, journal dans la maquette). Le panneau (Softphone.tsx) ne connaît que
 * ce contrat ; il n'y a jamais deux moteurs actifs à la fois, ni deux onglets qui en font tourner un : un onglet sans
 * le verrou du téléphone (useVerrouOnglet) a le moteur inerte creerMoteurAilleurs.
 */

/**
 * État de l'agent : `connexion` (chargement, SSO), `deconnecte` (session Connect absente ou expirée), `ailleurs`
 * (téléphone ouvert dans un autre onglet d'AGORA).
 */
export type EtatAgent = 'connexion' | 'deconnecte' | 'disponible' | 'absent' | 'occupe' | 'erreur' | 'ailleurs';

/** État de l'appel affiché : `numerotation` (sortant, en attente de réponse), `termine` (affiché quelques secondes). */
export type EtatAppel = 'sonnerie' | 'numerotation' | 'en-cours' | 'termine';

export interface AppelSoftphone {
  id: string;
  sens: 'entrant' | 'sortant';
  /** Numéro du correspondant (E.164), null si masqué. */
  numero: string | null;
  etat: EtatAppel;
  /** Instant du décroché (ms), pour le chronomètre ; absent tant que personne n'a décroché. */
  decrocheLe?: number;
  enAttente: boolean;
  muet: boolean;
}

export interface EtatSoftphone {
  agent: EtatAgent;
  appel: AppelSoftphone | null;
  /**
   * Renvoi d'appel (E.164) : les appels de l'agent sonnent sur ce numéro au lieu du navigateur (Connect : téléphone
   * fixe de l'agent) ; null = appels dans AGORA.
   */
  renvoi: string | null;
  /** Code d'erreur traduit par le panneau (service.telephonie.softphone.erreurs.<code>). */
  erreur?: CodeErreurSoftphone;
}

export type CodeErreurSoftphone = 'chargement' | 'acces' | 'appel' | 'action' | 'renvoi';

export interface MoteurSoftphone {
  /** Branche le moteur ; le CCP Connect (iframe) est placé, masqué, dans `conteneur`. */
  demarrer(conteneur: HTMLElement): void;
  arreter(): void;
  /** Reçoit l'état courant tout de suite, puis à chaque changement ; renvoie la fonction de désabonnement. */
  abonner(ecouteur: (e: EtatSoftphone) => void): () => void;
  /** Ouvre la connexion SSO (fenêtre Entra) quand la session Connect est absente ou expirée. */
  connecter(): void;
  changerDisponibilite(disponible: boolean): void;
  /** Appel sortant vers un numéro E.164. */
  appeler(numero: string): void;
  decrocher(): void;
  /** Raccroche l'appel en cours, ou refuse l'appel qui sonne. */
  raccrocher(): void;
  basculerAttente(): void;
  basculerMuet(): void;
  /** Touches du clavier pendant l'appel (serveurs vocaux). */
  envoyerTouches(touches: string): void;
  /** Transfert « à froid » vers un numéro E.164 : l'agent quitte l'appel une fois le numéro joint. */
  transferer(numero: string): void;
  /** Renvoie les appels de l'agent vers un numéro E.164, ou les ramène dans AGORA (null). */
  definirRenvoi(numero: string | null): void;
}

export const ETAT_INITIAL: EtatSoftphone = { agent: 'connexion', appel: null, renvoi: null };

/**
 * Moteur d'un onglet où le téléphone est ouvert dans un autre onglet : état `ailleurs`, aucune action – seul l'onglet
 * qui détient le verrou passe et reçoit les appels.
 */
export function creerMoteurAilleurs(): MoteurSoftphone {
  const diffusion = creerDiffusion({ ...ETAT_INITIAL, agent: 'ailleurs' });
  const rien = () => undefined;
  return {
    demarrer: rien, arreter: rien, abonner: diffusion.abonner, connecter: rien, changerDisponibilite: rien, appeler: rien, decrocher: rien,
    raccrocher: rien, basculerAttente: rien, basculerMuet: rien, envoyerTouches: rien, transferer: rien, definirRenvoi: rien,
  };
}

/** Diffusion de l'état aux abonnés (partagée par les deux moteurs). */
export function creerDiffusion(initial: EtatSoftphone = ETAT_INITIAL) {
  let etat = initial;
  const ecouteurs = new Set<(e: EtatSoftphone) => void>();
  return {
    lire: () => etat,
    publier(maj: Partial<EtatSoftphone>) {
      etat = { ...etat, ...maj };
      ecouteurs.forEach((f) => f(etat));
    },
    abonner(f: (e: EtatSoftphone) => void) {
      ecouteurs.add(f);
      f(etat);
      return () => { ecouteurs.delete(f); };
    },
  };
}

/** Numéro E.164 extrait d'un point de terminaison Connect (« +33… », « sip:+33…@… ») ; null si masqué. */
export function numeroDuPoint(brut: string | null | undefined): string | null {
  const m = (brut ?? '').match(/\+[1-9]\d{6,14}/);
  return m ? m[0] : null;
}
